import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';
import vaDebounce from 'platform/utilities/data/debounce';
import { useCombobox } from 'downshift-v9';
import Autosuggest from 'platform/site-wide/autosuggest';
import useServiceType, {
  FACILITY_TYPE_FILTERS,
} from '../../../hooks/useServiceType';
import { CMS_SERVICE, NO_MATCHING_VA_SERVICE_FOUND } from '../../../constants';

const ALL_VA_HEALTH_SERVICES = 'All VA health services';

// Records *why* a search runs without a user-selected specific service, so
// analytics (fl-search-svc-type) can distinguish these cases. Read at submit
// time by useSearchSubmit via draftFormState.serviceTypeIntent.
const SERVICE_TYPE_INTENT = {
  // User did not run a service search: never typed, only opened the dropdown,
  // or cleared the field back to blank.
  DEFAULT: 'default',
  // User typed a query that returned suggestions but did not pick a specific
  // service ("found" refers to the search returning results).
  FOUND_DEFAULT: 'found_default',
  // User typed a query that returned no suggestions.
  NOT_FOUND_DEFAULT: 'not_found_default',
  // User chose "All VA health services" from the list without a preceding
  // search (a direct pick). Picking it after a search keeps that search's
  // outcome instead (found_default / not_found_default).
  ALL: 'all',
};

const VAMCServiceAutosuggest = ({
  committedServiceDisplay,
  isMobile,
  onDraftChange,
  searchInitiated,
  setSearchInitiated,
}) => {
  const { selector, serviceTypeFilter } = useServiceType();
  const [inputValue, setInputValue] = useState(null);
  const [options, setOptions] = useState([]);
  const [allVAMCServices, setAllVAMCServices] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);

  // Track inputValue via ref for the committedServiceDisplay effect,
  // which needs to check inputValue without re-running when it changes
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  // Tracks the outcome of the user's most recent typed search: 'found',
  // 'not_found', or null (no active search — never typed, or cleared). Used so
  // that selecting "All VA health services" reflects how the user got there: a
  // direct pick is 'all', but picking it after a search keeps the search
  // outcome ('found_default' / 'not_found_default').
  const searchResultRef = useRef(null);

  const getServices = useCallback(
    input => {
      const services = serviceTypeFilter(
        input || null,
        FACILITY_TYPE_FILTERS.VAMC,
      );
      setSelectedItem(null);

      if (input && !services?.length && allVAMCServices?.length) {
        setOptions([
          {
            disabled: true,
            id: 'no-matching-services',
            toDisplay: NO_MATCHING_VA_SERVICE_FOUND,
            statusRole: 'status',
          },
          allVAMCServices?.[0],
        ]);
        // Don't highlight the "All VA health services" fallback. Leaving it as
        // the active descendant makes screen readers announce that option
        // before the "No matching services found" status row (a11y #142577).
        setHighlightedIndex(-1);
        searchResultRef.current = 'not_found';
        onDraftChange?.({
          serviceTypeIntent: SERVICE_TYPE_INTENT.NOT_FOUND_DEFAULT,
          // Typing invalidates any prior dropdown pick.
          selectionMethod: null,
        });
        return;
      }

      const serviceOptions = services.map(service => {
        let displayName = service?.[CMS_SERVICE.DISPLAY_NAME];

        if (displayName && service?.[CMS_SERVICE.ALIAS]) {
          displayName = `${displayName} (${service?.[CMS_SERVICE.ALIAS]})`;
        } else if (!displayName) {
          return null;
        }

        return {
          id: service[CMS_SERVICE.DISPLAY_NAME],
          serviceId: service[CMS_SERVICE.SERVICE_ID],
          toDisplay: displayName,
        };
      });

      if (serviceOptions?.length) {
        setOptions(serviceOptions);
        setHighlightedIndex(-1);
        setAllVAMCServices(prev => (prev.length ? prev : serviceOptions));

        // The user typed a query that returned suggestions but has not selected
        // a specific one yet; if they submit as-is it counts as 'found_default'.
        // Guarded on `input` so the no-arg initialization call (which lists the
        // full set, not a user search) does not touch draft state.
        if (input) {
          searchResultRef.current = 'found';
          onDraftChange?.({
            serviceTypeIntent: SERVICE_TYPE_INTENT.FOUND_DEFAULT,
            // Typing invalidates any prior dropdown pick.
            selectionMethod: null,
          });
        }
      }
    },
    [serviceTypeFilter, allVAMCServices, onDraftChange],
  );

  const debouncedGetServices = useMemo(
    () => vaDebounce(300, getServices),
    [getServices],
  );

  useEffect(() => {
    return () => {
      debouncedGetServices.cancel?.();
    };
  }, [debouncedGetServices]);

  // Initialize services when selector data becomes available
  useEffect(() => {
    if (selector?.data && !allVAMCServices.length) {
      getServices();
    }
  }, [selector, allVAMCServices.length, getServices]);

  // Handles edge cases where the form might be re-rendered between
  // viewpoints or for any other reason and the autosuggest input is lost.
  // Also clears input when committedServiceDisplay becomes null (e.g., facility type change)
  useEffect(() => {
    if (committedServiceDisplay) {
      // Only set if input is currently empty to avoid overwriting user input
      if (!inputValueRef.current) {
        setInputValue(committedServiceDisplay);
      }
    } else if (
      inputValueRef.current &&
      committedServiceDisplay === null &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      // Clear input when committedServiceDisplay is explicitly null
      // This happens when user changes facility type
      // Only clear if the input field is not currently focused (prevents clearing mid-typing)
      setInputValue(null);
      setOptions(allVAMCServices);
      setSelectedItem(null);
      setHighlightedIndex(-1);
    }
  }, [committedServiceDisplay, allVAMCServices]);

  // If the user has not typed a service at all, or types something that does not
  // match any of the services, we'll search for "All VA health services" when the
  // search button is clicked. This prefills the input and updates the form state
  // so the URL params reflect the actual service type being searched.
  useEffect(() => {
    if (!searchInitiated) return;

    const matchedService = options.find(
      service => service.toDisplay === inputValue,
    );

    if (!matchedService) {
      const allServicesOption = options.find(
        service => service.id === ALL_VA_HEALTH_SERVICES,
      );

      setInputValue(ALL_VA_HEALTH_SERVICES);

      if (allServicesOption) {
        onDraftChange?.({
          serviceType: allServicesOption.serviceId,
          vamcServiceDisplay: allServicesOption.toDisplay,
        });
      }
    }

    // The search outcome has now been reported, so clear it. A subsequent
    // "All VA health services" pick without a new search is a fresh 'all'
    // rather than carrying the previous search's found/not_found outcome.
    searchResultRef.current = null;

    setSearchInitiated(false);
  }, [options, searchInitiated, inputValue, onDraftChange, setSearchInitiated]);

  const handleClearClick = () => {
    debouncedGetServices.cancel?.();
    setInputValue(null);
    setOptions(allVAMCServices);
    setSelectedItem(null);
    setHighlightedIndex(-1);
    searchResultRef.current = null;

    onDraftChange?.({
      serviceType: null,
      vamcServiceDisplay: null,
      serviceTypeIntent: SERVICE_TYPE_INTENT.DEFAULT,
      selectionMethod: null,
    });

    if (inputRef?.current) {
      focusElement(inputRef.current);
    }
  };

  const handleInputValueChange = e => {
    // The autosuggest component runs both handleOnSelect and onInputValueChange
    // when a dropdown value is selected. This creates problems for this component,
    // so we limit this function's purpose to only handle typing and not selection
    if (e.type === useCombobox.stateChangeTypes.InputChange) {
      const userInput = e.inputValue?.trimStart();
      setInputValue(userInput);

      const selectedItemId = e?.selectedItem?.id;
      const selectedItemDisplay = e?.selectedItem?.toDisplay;

      if (userInput.length >= 2) {
        debouncedGetServices(
          selectedItemDisplay === e.inputValue ? selectedItemId : e.inputValue,
        );
      } else if (userInput.length === 0) {
        handleClearClick();
      } else {
        setOptions(allVAMCServices);
        setSelectedItem(null);
        setHighlightedIndex(-1);
      }
    } else if (
      e.type === useCombobox.stateChangeTypes.ItemClick &&
      !e.selectedItem
    ) {
      // no-op
    }
  };

  const handleDropdownSelection = useCallback(
    event => {
      const { selectedItem: selected } = event;
      if (selected?.toDisplay) {
        setInputValue(selected.toDisplay);
        setSelectedItem(selected);

        // Record *how* the option was picked, for analytics
        // (fl-search-svc-type-selection-search vs -via-keyboard). Applies to any
        // option, including "All VA health services". Downshift surfaces the
        // interaction via event.type: a mouse click on an item is ItemClick,
        // pressing Enter on the highlighted item is InputKeyDownEnter.
        let selectionMethod = null;
        if (event.type === useCombobox.stateChangeTypes.ItemClick) {
          selectionMethod = 'mouse';
        } else if (
          event.type === useCombobox.stateChangeTypes.InputKeyDownEnter
        ) {
          selectionMethod = 'keyboard';
        }

        debouncedGetServices.cancel?.();

        let serviceTypeIntent = null;
        if (selected.id === ALL_VA_HEALTH_SERVICES) {
          // Reset the visible dropdown so a stale "No matching services found"
          // status row or filtered subset from a prior typed search does not
          // persist after the user picks the default option (#22942).
          setOptions(allVAMCServices);
          setHighlightedIndex(-1);

          // Picking "All VA health services" reflects how the user got here:
          // directly (no search) is 'all', but reaching it after a search keeps
          // that search's outcome so the analytics still reflect the search.
          if (searchResultRef.current === 'not_found') {
            serviceTypeIntent = SERVICE_TYPE_INTENT.NOT_FOUND_DEFAULT;
          } else if (searchResultRef.current === 'found') {
            serviceTypeIntent = SERVICE_TYPE_INTENT.FOUND_DEFAULT;
          } else {
            serviceTypeIntent = SERVICE_TYPE_INTENT.ALL;
          }
        } else {
          // Choosing a specific service leaves serviceTypeIntent null so its
          // name is reported instead. It's also a definitive choice, so any
          // prior typed-search outcome no longer applies — clear it so a later
          // "All VA health services" pick is a fresh 'all'.
          searchResultRef.current = null;
        }

        onDraftChange?.({
          serviceType: selected.serviceId,
          vamcServiceDisplay: selected.toDisplay,
          serviceTypeIntent,
          selectionMethod,
        });
      }
    },
    [onDraftChange, allVAMCServices, debouncedGetServices],
  );

  useEffect(() => {
    if (!isMobile) return undefined;
    const servicesTypeInput = document.getElementById('vamc-services');
    if (!servicesTypeInput) return undefined;

    const handleFocus = () => {
      const servicesTypeContainer = document.getElementById(
        'vamc-services-autosuggest-container',
      );
      if (!servicesTypeContainer) return;

      setTimeout(() => {
        servicesTypeContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 300);
    };

    servicesTypeInput.addEventListener('focus', handleFocus);
    return () => servicesTypeInput.removeEventListener('focus', handleFocus);
  }, [isMobile]);
  return (
    <Autosuggest
      selectedItem={selectedItem}
      downshiftInputProps={{
        autoCorrect: 'off',
        disabled: false,
        spellCheck: 'false',
      }}
      handleOnSelect={handleDropdownSelection}
      highlightedIndex={highlightedIndex}
      hintText="Type a medical condition, treatment, or specialty to find services"
      inputId="vamc-services"
      inputRef={inputRef}
      inputValue={inputValue || ''}
      keepDataOnBlur={{ resetHighlightOnClear: true }}
      label={
        <>
          Select a VA health service{' '}
          <span className="form-required-span">(*Required)</span>
        </>
      }
      onClearClick={handleClearClick}
      clearButtonLabel="Clear service type"
      onHighlightedIndexChange={changes => {
        if (typeof changes.highlightedIndex === 'number') {
          setHighlightedIndex(changes.highlightedIndex);
        }
      }}
      onInputValueChange={handleInputValueChange}
      options={options}
      showDownCaret
      showError={false}
    />
  );
};

VAMCServiceAutosuggest.propTypes = {
  committedServiceDisplay: PropTypes.string,
  isMobile: PropTypes.bool,
  searchInitiated: PropTypes.bool,
  setSearchInitiated: PropTypes.func,
  onDraftChange: PropTypes.func,
};

export default VAMCServiceAutosuggest;
